import { dateKey } from '../nutrition/nutrition'

/**
 * Exercise tracking, built around one question: what weight should I put on the bar today?
 *
 * The answer comes from the last logged set. Complete it and the target goes up by one
 * increment; fail it and the target holds — so progression is recorded rather than
 * remembered.
 *
 * PURE — "now" is always passed in.
 */

export type BodyTag = 'upper' | 'lower'

export interface Exercise {
  id: string
  title: string
  description: string
  youtubeUrl: string | null
  tag: BodyTag
  createdAt: string
}

export interface SetLog {
  id: string
  exerciseId: string
  weightKg: number
  /** Did the full set get finished? This is what drives the next target. */
  completed: boolean
  date: string
  createdAt: string
}

export interface ExerciseState {
  exercises: Exercise[]
  logs: SetLog[]
}

export interface ExerciseProgress {
  exercise: Exercise
  lastLog: SetLog | null
  /** What to attempt next. Null until something has been logged. */
  targetKg: number | null
  /** Best completed weight ever, which is the number people actually brag about. */
  bestKg: number | null
  /** Change against the oldest completed set in the trend window. */
  changePct: number | null
  logCount: number
}

/** Smallest jump most gyms can actually make: 2 × 1.25 kg plates. */
export const INCREMENT_KG = 2.5

export const TREND_WINDOW_DAYS = 30

export function emptyExercise(): ExerciseState {
  return { exercises: [], logs: [] }
}

function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000)
}

export function logsFor(state: ExerciseState, exerciseId: string): SetLog[] {
  return state.logs
    .filter((l) => l.exerciseId === exerciseId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

/**
 * Completed the set? Go up. Failed it? Stay put and try again — repeating a weight is
 * progress too, and bumping the target after a failure is how people stall.
 */
export function nextTarget(lastLog: SetLog | null): number | null {
  if (!lastLog) return null
  return lastLog.completed ? lastLog.weightKg + INCREMENT_KG : lastLog.weightKg
}

export function progressFor(
  state: ExerciseState,
  exercise: Exercise,
  now: Date = new Date()
): ExerciseProgress {
  const logs = logsFor(state, exercise.id)
  const lastLog = logs.at(-1) ?? null
  const completed = logs.filter((l) => l.completed)
  const today = dateKey(now)

  const windowCompleted = completed.filter((l) => daysBetween(l.date, today) <= TREND_WINDOW_DAYS)
  const baseline = windowCompleted.length > 1 ? windowCompleted[0] : null
  const latestCompleted = windowCompleted.at(-1) ?? null

  return {
    exercise,
    lastLog,
    targetKg: nextTarget(lastLog),
    bestKg: completed.length > 0 ? Math.max(...completed.map((l) => l.weightKg)) : null,
    changePct:
      baseline && latestCompleted && baseline.weightKg !== 0
        ? ((latestCompleted.weightKg - baseline.weightKg) / baseline.weightKg) * 100
        : null,
    logCount: logs.length,
  }
}

export function allProgress(
  state: ExerciseState,
  tag: BodyTag | 'all',
  now: Date = new Date()
): ExerciseProgress[] {
  return state.exercises
    .filter((e) => tag === 'all' || e.tag === tag)
    .map((e) => progressFor(state, e, now))
    .sort((a, b) => {
      const aDate = a.lastLog?.createdAt ?? ''
      const bDate = b.lastLog?.createdAt ?? ''
      return bDate.localeCompare(aDate) || a.exercise.title.localeCompare(b.exercise.title)
    })
}

export function parseWeight(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.')
  if (trimmed === '') return null
  if (!/^\d*\.?\d*$/.test(trimmed)) return null
  const value = Number(trimmed)
  if (!Number.isFinite(value) || value <= 0) return null
  return Math.round(value * 100) / 100
}

export function formatKg(value: number | null): string {
  if (value === null) return '—'
  const rounded = Math.round(value * 100) / 100
  return `${rounded} kg`
}

/** Accepts the usual YouTube URL shapes and returns the id, or null if it isn't one. */
export function youtubeId(url: string | null): string | null {
  if (!url) return null
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{11})/,
    /(?:youtu\.be\/)([\w-]{11})/,
    /(?:youtube\.com\/embed\/)([\w-]{11})/,
    /(?:youtube\.com\/shorts\/)([\w-]{11})/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}
